/**
 * Lógica pura del wizard de inventario (épica C1: RES-35 … RES-38). Sin React, para testearla.
 *
 * Estado del wizard (lo que se guarda como borrador):
 *   {
 *     step, startedAt,
 *     restaurantIds: [],                                  // paso 0: locales con stock inicial
 *     rubroId,                                            // paso 1: rubro elegido
 *     items: [{ key, name, kind, unit, category, defaultMinStock, selected, source }],
 *     resale: { [productId]: { selected, quantity, target } },   // paso 2 (target: ref de insumo)
 *     recipes: { [productId]: { lines: [{ ref, quantity, unit }], deferred } },  // paso 3
 *     stock: { [itemKey]: { [restaurantId]: qty } },      // paso 4
 *     minStock: { [itemKey]: min },                       // paso 4: mínimo de la organización
 *     settings: { deductionTrigger, restockOnCancel, allowNegativeStock, costingMethod } | null
 *   }
 *
 * Referencias a insumos (`ref`): `new:{key}` para uno que crea el wizard (se resuelve por nombre
 * con el mapa que devuelve `POST /inventory/setup`), `cat:{itemId}` para uno del catálogo, y
 * `mirror` en el paso 2 para "crear insumo espejo" con el nombre del producto.
 */

import { normalizeUnit, familyUnits } from './units';
import { getInventoryTemplate } from '../data/inventoryTemplates';
import { RECIPE_TEMPLATES } from '../data/recipeTemplates';

export const WIZARD_STEPS = ['scope', 'items', 'resale', 'recipes', 'stock', 'policies', 'summary'];
export const WIZARD_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MIRROR_TARGET = 'mirror';
export const EXPANDED_RECIPES = 10;
const KINDS = ['INGREDIENT', 'RESALE', 'PACKAGING'];

/**
 * Texto normalizado para comparar nombres: minúsculas, sin tildes, espacios simples. Mismo
 * criterio que el backend (`normalizeName`), así la idempotencia por nombre coincide.
 * @param {string} value - Texto
 * @returns {string} Normalizado
 */
export function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export const newRef = (key) => `new:${key}`;
export const catalogRef = (itemId) => `cat:${itemId}`;

/**
 * Estado inicial del wizard.
 * @param {number} [now] - Timestamp
 * @returns {Object} Estado
 */
export function initialWizardState(now = Date.now()) {
  return {
    step: 0,
    startedAt: now,
    restaurantIds: [],
    rubroId: null,
    items: [],
    resale: {},
    recipes: {},
    stock: {},
    minStock: {},
    settings: null,
  };
}

// ─── Borrador ───────────────────────────────────────────────────────────────

/**
 * Clave de storage del borrador (uno por organización: el catálogo es de la organización).
 * @param {string} organizationId - Organización
 * @returns {string} Clave
 */
export const wizardDraftKey = (organizationId) =>
  `inventoryWizardDraft_${organizationId || 'none'}`;

/**
 * Serializa el borrador con la fecha de guardado.
 * @param {Object} state - Estado
 * @param {number} [now] - Timestamp
 * @returns {string} JSON
 */
export const serializeDraft = (state, now = Date.now()) => JSON.stringify({ savedAt: now, state });

/**
 * Lee un borrador: `null` si no hay, está corrupto o tiene más de 7 días.
 * @param {string|null} raw - Valor guardado
 * @param {number} [now] - Timestamp
 * @returns {Object|null} Estado
 */
export function parseDraft(raw, now = Date.now()) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.state || typeof parsed.savedAt !== 'number') return null;
    if (now - parsed.savedAt > WIZARD_DRAFT_TTL_MS) return null;
    return { ...initialWizardState(now), ...parsed.state };
  } catch {
    return null;
  }
}

/**
 * ¿El estado tiene algo cargado que valga la pena conservar? (La reventa sola no cuenta: el
 * paso 2 se precarga solo con lo que detecta en la carta.)
 * @param {Object} state - Estado
 * @returns {boolean} `true` si hay cambios
 */
export const hasWizardProgress = (state) =>
  Boolean(
    state &&
    (state.items.length || Object.keys(state.recipes).length || Object.keys(state.stock).length)
  );

// ─── Paso 1: insumos ────────────────────────────────────────────────────────

/**
 * Insumo del wizard.
 * @param {Object} raw - `{ name, kind?, unit, category?, defaultMinStock? }`
 * @param {string} source - `template` | `custom` | `csv`
 * @returns {Object} Insumo con `key` y `selected`
 */
export function makeWizardItem(raw, source) {
  const name = String(raw.name || '').trim();
  const kind = KINDS.includes(raw.kind) ? raw.kind : 'INGREDIENT';
  return {
    key: normalizeText(name),
    name,
    kind,
    unit: normalizeUnit(raw.unit) || 'un',
    category: raw.category ? String(raw.category).trim() : '',
    defaultMinStock: Number(raw.defaultMinStock) > 0 ? Number(raw.defaultMinStock) : 0,
    selected: true,
    source,
  };
}

/**
 * Suma insumos a la lista sin duplicar por nombre normalizado (el que ya estaba se conserva, y si
 * estaba desmarcado se vuelve a marcar).
 * @param {Object[]} current - Lista actual
 * @param {Object[]} incoming - Insumos a sumar
 * @returns {Object[]} Lista nueva
 */
export function mergeWizardItems(current, incoming) {
  const byKey = new Map(current.map((item) => [item.key, item]));
  const out = current.map((item) => ({ ...item }));
  for (const item of incoming) {
    if (!item.key) continue;
    if (byKey.has(item.key)) {
      const existing = out.find((i) => i.key === item.key);
      existing.selected = true;
      continue;
    }
    byKey.set(item.key, item);
    out.push(item);
  }
  return out;
}

/**
 * Insumos precargados de un rubro.
 * @param {string} rubroId - Rubro
 * @returns {Object[]} Insumos del wizard
 */
export function templateItems(rubroId) {
  const template = getInventoryTemplate(rubroId);
  return template ? template.items.map((raw) => makeWizardItem(raw, 'template')) : [];
}

/**
 * Filtra por nombre o categoría.
 * @param {Object[]} items - Insumos
 * @param {string} query - Búsqueda
 * @returns {Object[]} Filtrados
 */
export function filterWizardItems(items, query) {
  const q = normalizeText(query);
  if (!q) return items;
  return items.filter((item) => item.key.includes(q) || normalizeText(item.category).includes(q));
}

/**
 * Agrupa por categoría (orden alfabético, "sin categoría" al final).
 * @param {Object[]} items - Insumos
 * @returns {Array<{category: string, items: Object[]}>} Grupos
 */
export function groupByCategory(items) {
  const groups = new Map();
  for (const item of items) {
    const category = item.category || '';
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(item);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (!a ? 1 : !b ? -1 : a.localeCompare(b, 'es')))
    .map(([category, list]) => ({ category, items: list }));
}

/**
 * Parte una línea CSV (coma o punto y coma, comillas dobles opcionales).
 * @param {string} lineText - Línea
 * @param {string} separator - Separador
 * @returns {string[]} Celdas
 */
function splitCsvLine(lineText, separator) {
  const cells = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < lineText.length; i++) {
    const ch = lineText[i];
    if (ch === '"') {
      if (quoted && lineText[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        quoted = !quoted;
      }
    } else if (ch === separator && !quoted) {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

/**
 * Filas de un CSV con encabezado opcional. Detecta `;` si la primera línea tiene más `;` que `,`.
 * @param {string} text - Contenido
 * @param {string[]} columns - Columnas esperadas (en orden, si no hay encabezado)
 * @returns {Array<{line: number, values: Object}>} Filas
 */
function csvRows(text, columns) {
  const lines = String(text || '')
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .map((l, index) => ({ text: l, line: index + 1 }))
    .filter((l) => l.text.trim());
  if (!lines.length) return [];
  const first = lines[0].text;
  const separator = (first.match(/;/g) || []).length > (first.match(/,/g) || []).length ? ';' : ',';
  const header = splitCsvLine(first, separator).map(normalizeText);
  const aliases = {
    name: ['name', 'nombre', 'insumo', 'nome'],
    unit: ['unit', 'unidad', 'unidade'],
    category: ['category', 'categoria'],
    stock: ['stock', 'cantidad', 'qty', 'quantidade', 'estoque'],
    // RES-76: la ayuda en inglés pide la columna "minimum"; sin el alias se descartaba en silencio
    min: ['min', 'minimo', 'minimum', 'min stock', 'minstock', 'stock minimo', 'minimum stock'],
  };
  const hasHeader = header.some((cell) => aliases.name.includes(cell));
  const indexes = columns.map((col) =>
    hasHeader ? header.findIndex((cell) => aliases[col].includes(cell)) : columns.indexOf(col)
  );
  return lines.slice(hasHeader ? 1 : 0).map(({ text: lineText, line }) => {
    const cells = splitCsvLine(lineText, separator);
    const values = {};
    columns.forEach((col, i) => {
      values[col] = indexes[i] >= 0 ? cells[indexes[i]] || '' : '';
    });
    return { line, values };
  });
}

/**
 * Importa insumos desde un CSV `name, unit, category`.
 * @param {string} text - Contenido del archivo
 * @returns {{items: Object[], errors: Array<{line: number, code: string, value?: string}>}} Resultado
 */
export function parseCatalogCsv(text) {
  const items = [];
  const errors = [];
  for (const { line, values } of csvRows(text, ['name', 'unit', 'category'])) {
    if (!values.name) {
      errors.push({ line, code: 'missingName' });
      continue;
    }
    const unit = normalizeUnit(values.unit);
    if (!unit) {
      errors.push({ line, code: 'invalidUnit', value: values.unit });
      continue;
    }
    items.push(makeWizardItem({ name: values.name, unit, category: values.category }, 'csv'));
  }
  return { items, errors };
}

/**
 * Importa stock inicial desde un CSV `name, stock[, min]`.
 * @param {string} text - Contenido del archivo
 * @returns {{rows: Array<{key: string, stock: number|null, min: number|null}>, errors: Object[]}} Resultado
 */
export function parseStockCsv(text) {
  const rows = [];
  const errors = [];
  const num = (value) => {
    if (value === '' || value === undefined) return null;
    const n = Number(String(value).replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : NaN;
  };
  for (const { line, values } of csvRows(text, ['name', 'stock', 'min'])) {
    if (!values.name) {
      errors.push({ line, code: 'missingName' });
      continue;
    }
    const stock = num(values.stock);
    const min = num(values.min);
    if (Number.isNaN(stock) || Number.isNaN(min)) {
      errors.push({ line, code: 'invalidNumber', value: values.stock });
      continue;
    }
    rows.push({ key: normalizeText(values.name), stock, min });
  }
  return { rows, errors };
}

// ─── Carta ──────────────────────────────────────────────────────────────────

/**
 * Productos activos de la carta (sin borrados), con su categoría y orden de carta.
 * @param {Object[]} menu - Respuesta de `getMenu().menu`
 * @returns {Object[]} Productos
 */
export function flattenMenu(menu) {
  const out = [];
  let order = 0;
  for (const category of menu || []) {
    if (category.status === 'deleted') continue;
    for (const item of category.items || []) {
      if (item.status === 'deleted') continue;
      out.push({
        itemId: item.itemId,
        name: item.name,
        categoryId: category.categoryId,
        categoryName: category.name || '',
        tags: Array.isArray(item.tags) ? item.tags : [],
        popularityScore: Number(item.popularityScore) || 0,
        isCombo: Boolean(item.isCombo),
        inventoryLink: item.inventoryLink || null,
        recipe: Array.isArray(item.recipe) ? item.recipe : [],
        order: order++,
      });
    }
  }
  return out;
}

/** Palabras que delatan una categoría de reventa (bebidas y envasados). */
export const RESALE_KEYWORDS = [
  'bebida',
  'gaseosa',
  'cerveza',
  'vino',
  'trago',
  'agua',
  'jugo',
  'envasado',
  'drink',
];

/**
 * ¿La categoría o los tags del producto son de reventa?
 * @param {Object} product - Producto de `flattenMenu`
 * @returns {boolean} `true` si parece bebida / envasado
 */
export function looksLikeResale(product) {
  if (product.isCombo) return false;
  const haystacks = [product.categoryName, ...product.tags].map(normalizeText);
  return haystacks.some((text) =>
    RESALE_KEYWORDS.some((word) => text.split(/[^a-z0-9]+/).some((token) => token.startsWith(word)))
  );
}

/** ¿El producto ya descuenta stock (vínculo o receta con alguna línea válida)? */
export const hasInventoryLink = (product) =>
  Boolean(product.inventoryLink && product.inventoryLink.inventoryItemId);
export const hasRecipe = (product) =>
  (product.recipe || []).some((line) => line && line.inventoryItemId && Number(line.quantity) > 0);

/**
 * Estado inicial del paso 2: productos detectados como reventa, marcados con insumo espejo.
 * Los que ya tienen vínculo no se proponen (se muestran como "ya controlado").
 * @param {Object[]} products - Productos de la carta
 * @returns {Object} `{ [productId]: { selected, quantity, target } }`
 */
export function detectResale(products) {
  const out = {};
  for (const product of products) {
    if (hasInventoryLink(product) || !looksLikeResale(product)) continue;
    out[product.itemId] = { selected: true, quantity: 1, target: MIRROR_TARGET };
  }
  return out;
}

/**
 * Productos para el paso 3: sin combos, sin los que van por reventa y sin los que ya descuentan,
 * ordenados por popularidad (y orden de carta como desempate / fallback).
 * @param {Object[]} products - Productos
 * @param {Object} resale - Estado del paso 2
 * @returns {Object[]} Productos
 */
export function recipeCandidates(products, resale) {
  return products
    .filter(
      (p) =>
        !p.isCombo &&
        !hasInventoryLink(p) &&
        !hasRecipe(p) &&
        !(resale[p.itemId] && resale[p.itemId].selected)
    )
    .sort((a, b) => b.popularityScore - a.popularityScore || a.order - b.order);
}

// ─── Paso 3: sugerencias ────────────────────────────────────────────────────

/**
 * Receta sugerida para un producto (match más largo gana).
 * @param {string} productName - Nombre del producto
 * @returns {Object|null} Sugerencia
 */
export function findRecipeSuggestion(productName) {
  const name = normalizeText(productName);
  let best = null;
  let bestLength = 0;
  for (const template of RECIPE_TEMPLATES) {
    for (const match of template.match) {
      const m = normalizeText(match);
      if (m.length > bestLength && name.includes(m)) {
        best = template;
        bestLength = m.length;
      }
    }
  }
  return best;
}

/**
 * Busca la plantilla de un insumo por nombre en todos los rubros (para sumarlo si falta).
 * @param {string} name - Nombre del insumo
 * @param {Object[]} templates - Rubros
 * @returns {Object|null} Insumo crudo
 */
function findTemplateItem(name, templates) {
  const key = normalizeText(name);
  for (const template of templates) {
    const found = template.items.find((item) => normalizeText(item.name) === key);
    if (found) return found;
  }
  return null;
}

/**
 * Convierte una sugerencia en líneas de receta. Cada ingrediente se busca entre los insumos
 * elegidos, después en el catálogo; si no está, se devuelve como insumo a sumar al paso 1.
 * @param {Object} suggestion - Sugerencia
 * @param {{items: Object[], catalog: Object[], templates: Object[]}} ctx - Insumos disponibles
 * @returns {{lines: Object[], addItems: Object[]}} Líneas y insumos a sumar
 */
export function applySuggestion(suggestion, { items, catalog, templates }) {
  const lines = [];
  const addItems = [];
  for (const line of suggestion.lines) {
    const key = normalizeText(line.ingredient);
    const inWizard = items.find((item) => item.key === key);
    const inCatalog = !inWizard && catalog.find((item) => normalizeText(item.name) === key);
    let ref;
    let unit;
    if (inWizard) {
      ref = newRef(key);
      unit = inWizard.unit;
    } else if (inCatalog) {
      ref = catalogRef(inCatalog.itemId);
      unit = normalizeUnit(inCatalog.unit) || 'un';
    } else {
      const raw = findTemplateItem(line.ingredient, templates) || {
        name: line.ingredient,
        unit: line.unit,
      };
      const item = makeWizardItem(raw, 'template');
      addItems.push(item);
      ref = newRef(item.key);
      unit = item.unit;
    }
    // La unidad de la línea tiene que ser de la familia del insumo (RES-17)
    const lineUnit = familyUnits(unit).includes(line.unit) ? line.unit : unit;
    lines.push({ ref, quantity: line.quantity, unit: lineUnit });
  }
  return { lines, addItems };
}

/**
 * Opciones de insumos para las recetas: los elegidos en el paso 1 y el catálogo existente (sin
 * duplicar por nombre, gana el del catálogo porque ya tiene id).
 * @param {Object[]} items - Insumos del wizard
 * @param {Object[]} catalog - Catálogo
 * @returns {Array<{ref: string, name: string, unit: string}>} Opciones
 */
export function ingredientOptions(items, catalog) {
  const catalogKeys = new Set(catalog.map((item) => normalizeText(item.name)));
  const fromCatalog = catalog
    .filter((item) => item.isActive !== false)
    .map((item) => ({
      ref: catalogRef(item.itemId),
      name: item.name,
      unit: normalizeUnit(item.unit) || 'un',
    }));
  const fromWizard = items
    .filter((item) => item.selected && !catalogKeys.has(item.key))
    .map((item) => ({ ref: newRef(item.key), name: item.name, unit: item.unit }));
  return [...fromWizard, ...fromCatalog].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/**
 * Líneas completas de una receta (insumo, cantidad > 0 y unidad válida para su familia).
 * @param {Object} recipe - `{ lines }`
 * @param {Array} options - `ingredientOptions`
 * @returns {Object[]} Líneas válidas
 */
export function validRecipeLines(recipe, options) {
  const byRef = new Map(options.map((opt) => [opt.ref, opt]));
  return ((recipe && recipe.lines) || []).filter((line) => {
    const option = byRef.get(line.ref);
    return (
      option &&
      Number(line.quantity) > 0 &&
      familyUnits(option.unit).includes(normalizeUnit(line.unit))
    );
  });
}

// ─── Paso 4 y armado final ──────────────────────────────────────────────────

/**
 * Insumos que va a crear (o reutilizar) el wizard: los marcados del paso 1 más los espejos de
 * reventa del paso 2, sin duplicar por nombre.
 * @param {Object} state - Estado
 * @param {Object[]} products - Productos de la carta
 * @returns {Object[]} Insumos del wizard
 */
export function catalogToCreate(state, products) {
  const selected = state.items.filter((item) => item.selected);
  const mirrors = [];
  for (const product of products) {
    const row = state.resale[product.itemId];
    if (!row || !row.selected || row.target !== MIRROR_TARGET) continue;
    mirrors.push(
      makeWizardItem(
        { name: product.name, kind: 'RESALE', unit: 'un', category: product.categoryName },
        'mirror'
      )
    );
  }
  return mergeWizardItems(selected, mirrors).filter((item) => item.selected);
}

/**
 * Body de `POST /inventory/setup`.
 * @param {Object} state - Estado
 * @param {Object[]} products - Productos de la carta
 * @param {{restaurantId: string, settingsChanges: Object|null}} ctx - Local actual y políticas cambiadas
 * @returns {Object} Body
 */
export function buildSetupPayload(state, products, { restaurantId, settingsChanges }) {
  const items = catalogToCreate(state, products);
  const initialStock = {};
  for (const item of items) {
    const perLocal = state.stock[item.key] || {};
    for (const rid of state.restaurantIds) {
      const qty = Number(perLocal[rid]);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      if (!initialStock[rid]) initialStock[rid] = {};
      initialStock[rid][item.name] = qty;
    }
  }
  const deferred = Object.entries(state.recipes)
    .filter(([, recipe]) => recipe && recipe.deferred)
    .map(([productId]) => productId);
  return {
    catalogItems: items.map(({ key, name, kind, unit, category, defaultMinStock }) => {
      const min = Number((state.minStock || {})[key]);
      return {
        name,
        kind,
        unit,
        ...(category ? { category } : {}),
        defaultMinStock: Number.isFinite(min) && min >= 0 ? min : defaultMinStock,
      };
    }),
    ...(Object.keys(initialStock).length ? { initialStock } : {}),
    ...(settingsChanges ? { settings: settingsChanges } : {}),
    ...(deferred.length && restaurantId ? { deferredProducts: { [restaurantId]: deferred } } : {}),
  };
}

/**
 * Resuelve una referencia de insumo a su itemId después del alta masiva.
 * @param {string} ref - `new:{key}` | `cat:{itemId}`
 * @param {{itemIds: Object, items: Object[]}} ctx - Mapa nombre → itemId y los insumos del wizard
 * @returns {string|null} itemId
 */
export function resolveRef(ref, { itemIds, items }) {
  if (!ref) return null;
  if (ref.startsWith('cat:')) return ref.slice(4);
  if (ref.startsWith('new:')) {
    const key = ref.slice(4);
    const item = items.find((i) => i.key === key);
    const byName = Object.entries(itemIds || {}).find(([name]) => normalizeText(name) === key);
    return (item && itemIds[item.name]) || (byName && byName[1]) || null;
  }
  return null;
}

/**
 * Cambios en la carta que el admin graba después del alta masiva: vínculos de reventa y recetas.
 * @param {Object} state - Estado
 * @param {Object[]} products - Productos
 * @param {{itemIds: Object, catalog: Object[]}} ctx - Mapa devuelto por el backend y catálogo
 * @returns {Array<{productId: string, productName: string, kind: 'link'|'recipe', data?: Object, error?: string}>} Operaciones
 */
export function buildMenuOperations(state, products, { itemIds, catalog }) {
  const items = catalogToCreate(state, products);
  const options = ingredientOptions(items, catalog);
  const optionByRef = new Map(options.map((opt) => [opt.ref, opt]));
  const ops = [];
  for (const product of products) {
    const row = state.resale[product.itemId];
    if (row && row.selected) {
      const ref = row.target === MIRROR_TARGET ? newRef(normalizeText(product.name)) : row.target;
      const inventoryItemId = resolveRef(ref, { itemIds, items });
      ops.push({
        productId: product.itemId,
        productName: product.name,
        kind: 'link',
        ...(inventoryItemId
          ? {
              data: {
                inventoryLink: {
                  inventoryItemId,
                  quantity: Number(row.quantity) > 0 ? Number(row.quantity) : 1,
                  unit: 'un',
                },
              },
            }
          : { error: 'unresolved' }),
      });
      continue;
    }
    const recipe = state.recipes[product.itemId];
    if (!recipe || recipe.deferred) continue;
    const lines = validRecipeLines(recipe, options);
    if (!lines.length) continue;
    const resolved = lines.map((line) => ({
      inventoryItemId: resolveRef(line.ref, { itemIds, items }),
      quantity: Number(line.quantity),
      unit: normalizeUnit(line.unit),
      name: optionByRef.get(line.ref).name,
    }));
    ops.push({
      productId: product.itemId,
      productName: product.name,
      kind: 'recipe',
      ...(resolved.every((l) => l.inventoryItemId)
        ? { data: { recipe: resolved } }
        : { error: 'unresolved' }),
    });
  }
  return ops;
}

/**
 * Números del resumen: "Se van a crear N insumos, N vínculos, N recetas, stock en N locales".
 * @param {Object} state - Estado
 * @param {Object[]} products - Productos
 * @param {Object[]} catalog - Catálogo
 * @returns {{items: number, links: number, recipes: number, restaurants: number, deferred: number}} Resumen
 */
export function summarizeWizard(state, products, catalog) {
  const items = catalogToCreate(state, products);
  const options = ingredientOptions(items, catalog);
  const links = products.filter((p) => state.resale[p.itemId] && state.resale[p.itemId].selected);
  const linked = new Set(links.map((p) => p.itemId));
  const recipes = products.filter(
    (p) =>
      !linked.has(p.itemId) &&
      state.recipes[p.itemId] &&
      !state.recipes[p.itemId].deferred &&
      validRecipeLines(state.recipes[p.itemId], options).length > 0
  );
  const restaurants = state.restaurantIds.filter((rid) =>
    items.some((item) => Number((state.stock[item.key] || {})[rid]) > 0)
  );
  return {
    items: items.length,
    links: links.length,
    recipes: recipes.length,
    restaurants: restaurants.length,
    deferred: Object.values(state.recipes).filter((r) => r && r.deferred).length,
  };
}
